import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Loader2, Star, MapPin, CheckCircle2, Building2, Home, Clock, CalendarClock, FileText, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { labelOf, SENIORITY, ARRANGEMENTS, EMPLOYMENT_TYPES } from "@/lib/jobFields";

export default function JobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [j, setJ] = useState(null);
  const [applying, setApplying] = useState(false);

  const load = () => api.get(`/jobs/${id}`).then(({ data }) => setJ(data)).catch(() => {});
  useEffect(() => { load(); }, [id]);

  const apply = async () => {
    setApplying(true);
    try {
      await api.post(`/jobs/${id}/interest`, { note: "" });
      toast.success("Interest signaled — the recruiter has been notified");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setApplying(false); }
  };

  if (!j) return <div className="h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  return (
    <div>
      <PageHeader overline={j.organization_name} title={j.title}>
        <Button variant="ghost" onClick={() => navigate("/candidate/opportunities")} className="rounded-none"><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button>
      </PageHeader>
      <div className="p-8 max-w-3xl">
        <div className="border border-black/10 bg-white p-8">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[0.6rem] font-mono uppercase tracking-wider px-2 py-0.5 border border-black/10 text-muted-foreground">{j.role_category}</span>
            <span className="text-[0.6rem] font-mono uppercase tracking-wider px-2 py-0.5 border border-black/10 text-muted-foreground">{labelOf(EMPLOYMENT_TYPES, j.employment_type)}</span>
            {j.seniority && <span className="text-[0.6rem] font-mono uppercase tracking-wider px-2 py-0.5 border border-black/10 text-muted-foreground">{labelOf(SENIORITY, j.seniority)}</span>}
            {j.featured && <span className="flex items-center gap-1 text-[0.6rem] font-mono text-primary px-2 py-0.5 border border-primary/40"><Star className="h-3 w-3 fill-primary" /> FEATURED</span>}
          </div>
          <h1 className="font-heading font-bold text-2xl mt-4">{j.title}</h1>
          <div className="flex flex-wrap gap-x-8 gap-y-2 mt-3 text-sm text-muted-foreground">
            <span className="flex items-center gap-1"><Building2 className="h-4 w-4" /> {j.organization_name}</span>
            {j.location && <span className="flex items-center gap-1"><MapPin className="h-4 w-4" /> {j.location}</span>}
            {j.work_arrangement && <span className="flex items-center gap-1"><Home className="h-4 w-4" /> {labelOf(ARRANGEMENTS, j.work_arrangement)}</span>}
            {j.compensation_range && <span>{j.compensation_range}</span>}
            {j.application_deadline && <span className="flex items-center gap-1"><CalendarClock className="h-4 w-4" /> Apply by {j.application_deadline}</span>}
          </div>
          <div className="mt-6 border-t border-black/10 pt-6">
            <p className="overline mb-3">About the role</p>
            <p className="text-sm text-[#27272A] leading-relaxed whitespace-pre-wrap">{j.description || "No description provided."}</p>
          </div>
          {j.responsibilities && (
            <div className="mt-6"><p className="overline mb-3">Key responsibilities</p>
              <p className="text-sm text-[#27272A] leading-relaxed whitespace-pre-wrap">{j.responsibilities}</p></div>
          )}
          {j.requirements && (
            <div className="mt-6"><p className="overline mb-3">Requirements</p>
              <p className="text-sm text-[#27272A] leading-relaxed whitespace-pre-wrap">{j.requirements}</p></div>
          )}
          {j.attachment_url && (
            <div className="mt-6">
              <a href={`${process.env.REACT_APP_BACKEND_URL}${j.attachment_url}`} target="_blank" rel="noreferrer" data-testid="job-attachment-link"
                className="inline-flex items-center gap-2 text-sm text-primary hover:underline border border-black/10 px-4 py-2.5">
                <FileText className="h-4 w-4" /> {j.attachment_filename || "Job description (PDF)"}
              </a>
            </div>
          )}
          <div className="mt-8">
            {j.apply_method === "external" && j.apply_link ? (
              <a href={j.apply_link.includes("@") ? `mailto:${j.apply_link}` : j.apply_link} target="_blank" rel="noreferrer" data-testid="external-apply-btn"
                className="inline-flex items-center gap-2 rounded-none bg-primary text-white hover:bg-[#09090B] h-11 px-8 text-sm font-medium">
                Apply externally <ExternalLink className="h-4 w-4" />
              </a>
            ) : j.my_interest ? (
              <div className="flex items-center gap-2 text-[#10B981] text-sm font-medium"><CheckCircle2 className="h-5 w-5" /> You've signaled interest — status: {j.my_interest.status}</div>
            ) : (
              <Button onClick={apply} disabled={applying} data-testid="signal-interest-btn" className="rounded-none bg-primary hover:bg-[#09090B] h-11 px-8">
                {applying ? <Loader2 className="h-4 w-4 animate-spin" /> : "Signal interest"}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
